import { useState, useEffect, useMemo, useCallback } from 'react'
import { useClientAuth } from '../../context/ClientAuthContext'
import { useClientSync } from '../../hooks/useClientSync'
import {
  DollarSign, Bot, Server, FileText, Phone, PhoneForwarded,
  Voicemail, PhoneOff, PhoneMissed, Ban, UserX, Activity,
  Clock, WifiOff, AlertTriangle, TrendingUp, Calendar, Wifi,
  ChevronLeft, ChevronRight, Music
} from 'lucide-react'
import './ClientPortal.css'

export default function ClientDashboard() {
  const { client, setClient, token, clientApi } = useClientAuth()
  const [billing, setBilling] = useState(null)
  const [dialers, setDialers] = useState([])
  const [requests, setRequests] = useState([])
  const [bots, setBots] = useState([])
  const [sampleRecordings, setSampleRecordings] = useState([])
  const [loading, setLoading] = useState(true)
  const [syncToast, setSyncToast] = useState(null)
  const [callStatsDate, setCallStatsDate] = useState(new Date().toISOString().split('T')[0])
  const [callStatsLoading, setCallStatsLoading] = useState(false)

  // SSE Sync Handlers
  const handleBalanceUpdate = useCallback((data) => {
    setClient({ balance: data.balance_after })
    setBilling(prev => prev ? { ...prev, balance: data.balance_after } : prev)
    setSyncToast({ type: 'balance', message: `Balance updated: $${data.balance_after.toFixed(2)}` })
    setTimeout(() => setSyncToast(null), 3000)
  }, [setClient])

  const handleGracePeriodUpdate = useCallback((data) => {
    setClient({
      grace_period_active: data.grace_period_active,
      admin_extension_days: data.extension_days
    })
    setBilling(prev => prev ? {
      ...prev,
      grace_period: {
        ...prev.grace_period,
        active: data.grace_period_active,
        extension_days: data.extension_days
      }
    } : prev)
    setSyncToast({ type: 'grace', message: data.grace_period_active ? 'Grace period extended' : 'Grace period cleared' })
    setTimeout(() => setSyncToast(null), 3000)
  }, [setClient])

  const handleDialerAssigned = useCallback((data) => {
    // Refresh dialers list
    clientApi('/dialers').then(res => {
      if (res.success) setDialers(res.dialers)
    })
    setSyncToast({ type: 'dialer', message: 'New dialer assigned' })
    setTimeout(() => setSyncToast(null), 3000)
  }, [clientApi])

  const handleDialerRemoved = useCallback((data) => {
    setDialers(prev => prev.filter(d => d.id !== data.dialer_id))
    setSyncToast({ type: 'dialer', message: 'Dialer removed' })
    setTimeout(() => setSyncToast(null), 3000)
  }, [])

  const handleRequestCompleted = useCallback((data) => {
    setRequests(prev => prev.filter(r => r.id !== data.request_id))
    setSyncToast({ type: 'request', message: `Request ${data.status}` })
    setTimeout(() => setSyncToast(null), 3000)
  }, [])

  // SSE Connection
  const { connected: sseConnected, reconnecting } = useClientSync({
    token,
    onBalanceUpdate: handleBalanceUpdate,
    onGracePeriodUpdate: handleGracePeriodUpdate,
    onDialerAssigned: handleDialerAssigned,
    onDialerRemoved: handleDialerRemoved,
    onRequestCompleted: handleRequestCompleted,
    enabled: true
  })

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      const [billingData, dialersData, requestsData, botsData, recordingsData] = await Promise.all([
        clientApi('/billing'),
        clientApi('/dialers'),
        clientApi('/requests?status=pending'),
        clientApi('/bots'), // Fetch today's bot activity
        clientApi('/sample-recordings')
      ])

      if (billingData.success) setBilling(billingData.billing)
      if (dialersData.success) setDialers(dialersData.dialers)
      if (requestsData.success) setRequests(requestsData.requests)
      if (botsData.success) setBots(botsData.bots)
      if (recordingsData?.success) setSampleRecordings(recordingsData.recordings || [])
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
    }
    setLoading(false)
  }

  // Reload call stats when date changes
  const loadCallStats = async (date) => {
    setCallStatsLoading(true)
    try {
      const botsData = await clientApi(`/bots?from=${date}&to=${date}`)
      if (botsData.success) setBots(botsData.bots)
    } catch (error) {
      console.error('Failed to load call stats:', error)
    }
    setCallStatsLoading(false)
  }

  const handleDateChange = (newDate) => {
    const today = new Date().toISOString().split('T')[0]
    if (newDate > today) return // Don't allow future dates
    setCallStatsDate(newDate)
    loadCallStats(newDate)
  }

  const shiftDate = (days) => {
    const d = new Date(callStatsDate + 'T00:00:00')
    d.setDate(d.getDate() + days)
    handleDateChange(d.toISOString().split('T')[0])
  }

  const isToday = callStatsDate === new Date().toISOString().split('T')[0]

  const formatStatsDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00')
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
  }

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    })
  }

  // Calculate bot status stats
  const statusStats = useMemo(() => {
    const stats = { active: 0, waiting: 0, offline: 0, dial_level_0: 0 }
    bots.forEach(bot => {
      const status = bot.effective_status || 'offline'
      if (status === 'active' || status === 'running') stats.active++
      else if (status === 'waiting') stats.waiting++
      else if (status === 'dial_level_0') stats.dial_level_0++
      else stats.offline++
    })
    return stats
  }, [bots])

  // Calculate call totals for today
  const callTotals = useMemo(() => {
    return bots.reduce((acc, bot) => {
      acc.calls += Number(bot.calls) || 0
      acc.xfer += Number(bot.xfer) || 0
      acc.am += Number(bot.am) || 0
      acc.ni += Number(bot.ni) || 0
      acc.na += Number(bot.na) || 0
      acc.dnc += Number(bot.dnc) || 0
      acc.dnq += Number(bot.dnq) || 0
      return acc
    }, { calls: 0, xfer: 0, am: 0, ni: 0, na: 0, dnc: 0, dnq: 0 })
  }, [bots])

  const totalBots = bots.length
  const totalActiveBots = statusStats.active + statusStats.waiting

  // Calculate percentages for status bar
  const getStatusPercentage = (count) => {
    if (totalBots === 0) return 0
    return (count / totalBots) * 100
  }

  if (loading) {
    return (
      <div className="client-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    )
  }

  return (
    <div className="client-dashboard">
      {/* Sync Toast */}
      {syncToast && (
        <div className="client-sync-toast">
          <Wifi size={14} />
          <span>{syncToast.message}</span>
        </div>
      )}

      <div className="client-page-header">
        <h1>Welcome back, {client?.name}</h1>
        <p>Here's an overview of your account</p>
      </div>

      {/* Primary Stat Cards */}
      <div className="client-stat-blocks">
        <div className={`client-stat-block ${parseFloat(billing?.balance) < 0 ? 'red' : 'green'}`}>
          <div className="client-stat-icon">
            <DollarSign size={22} />
          </div>
          <div className="client-stat-content">
            <div className="client-stat-value">{formatCurrency(billing?.balance)}</div>
            <div className="client-stat-label">Account Balance</div>
          </div>
        </div>

        <div className="client-stat-block blue">
          <div className="client-stat-icon">
            <Bot size={22} />
          </div>
          <div className="client-stat-content">
            <div className="client-stat-value">{totalActiveBots} / {totalBots}</div>
            <div className="client-stat-label">Active Bots</div>
          </div>
        </div>

        <div className="client-stat-block cyan">
          <div className="client-stat-icon">
            <Phone size={22} />
          </div>
          <div className="client-stat-content">
            <div className="client-stat-value">{callTotals.calls.toLocaleString()}</div>
            <div className="client-stat-label">Calls Today</div>
          </div>
        </div>

        <div className="client-stat-block purple">
          <div className="client-stat-icon">
            <PhoneForwarded size={22} />
          </div>
          <div className="client-stat-content">
            <div className="client-stat-value">{callTotals.xfer.toLocaleString()}</div>
            <div className="client-stat-label">Transfers Today</div>
          </div>
        </div>
      </div>

      {/* Bot Status Overview */}
      <div className="client-section">
        <div className="client-table-container">
          <div className="client-table-header">
            <h2>Bot Status Overview</h2>
            <span className="client-table-subtitle">Real-time status of your bots</span>
          </div>
          <div className="dashboard-status-content">
            {/* Status Bar */}
            <div className="dashboard-status-bar">
              {statusStats.active > 0 && (
                <div
                  className="dashboard-status-segment active"
                  style={{ width: `${getStatusPercentage(statusStats.active)}%` }}
                  title={`${statusStats.active} Active`}
                />
              )}
              {statusStats.waiting > 0 && (
                <div
                  className="dashboard-status-segment waiting"
                  style={{ width: `${getStatusPercentage(statusStats.waiting)}%` }}
                  title={`${statusStats.waiting} Waiting`}
                />
              )}
              {statusStats.dial_level_0 > 0 && (
                <div
                  className="dashboard-status-segment dial-level-0"
                  style={{ width: `${getStatusPercentage(statusStats.dial_level_0)}%` }}
                  title={`${statusStats.dial_level_0} Dial Level 0`}
                />
              )}
              {statusStats.offline > 0 && (
                <div
                  className="dashboard-status-segment offline"
                  style={{ width: `${getStatusPercentage(statusStats.offline)}%` }}
                  title={`${statusStats.offline} Offline`}
                />
              )}
            </div>

            {/* Status Cards */}
            <div className="dashboard-status-cards">
              <div className="dashboard-status-card active">
                <Activity size={16} />
                <span className="status-count">{statusStats.active}</span>
                <span className="status-label">Active</span>
              </div>
              <div className="dashboard-status-card waiting">
                <Clock size={16} />
                <span className="status-count">{statusStats.waiting}</span>
                <span className="status-label">Waiting</span>
              </div>
              <div className="dashboard-status-card dial-level-0">
                <AlertTriangle size={16} />
                <span className="status-count">{statusStats.dial_level_0}</span>
                <span className="status-label">Dial Lvl 0</span>
              </div>
              <div className="dashboard-status-card offline">
                <WifiOff size={16} />
                <span className="status-count">{statusStats.offline}</span>
                <span className="status-label">Offline</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call Activity */}
      <div className="client-section">
        <div className="client-table-container">
          <div className="client-table-header">
            <h2>{isToday ? "Today's Call Activity" : 'Call Activity'}</h2>
            <div className="call-stats-date-nav">
              <button className="date-nav-btn" onClick={() => shiftDate(-1)} title="Previous day">
                <ChevronLeft size={16} />
              </button>
              <input
                type="date"
                value={callStatsDate}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => e.target.value && handleDateChange(e.target.value)}
                className="date-nav-picker"
              />
              <button className="date-nav-btn" onClick={() => shiftDate(1)} disabled={isToday} title={isToday ? 'Already on today' : 'Next day'}>
                <ChevronRight size={16} />
              </button>
              <button className="date-nav-today-btn" onClick={() => handleDateChange(new Date().toISOString().split('T')[0])} style={isToday ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                Today
              </button>
              {callStatsLoading && <div className="date-nav-spinner" />}
            </div>
          </div>
          <div className="dashboard-dispo-grid">
            <div className="dashboard-dispo-card calls">
              <div className="dispo-icon"><Phone size={18} /></div>
              <div className="dispo-value">{callTotals.calls.toLocaleString()}</div>
              <div className="dispo-label">Total Calls</div>
            </div>
            <div className="dashboard-dispo-card xfer">
              <div className="dispo-icon"><PhoneForwarded size={18} /></div>
              <div className="dispo-value">{callTotals.xfer.toLocaleString()}</div>
              <div className="dispo-label">Transfers</div>
            </div>
            <div className="dashboard-dispo-card am">
              <div className="dispo-icon"><Voicemail size={18} /></div>
              <div className="dispo-value">{callTotals.am.toLocaleString()}</div>
              <div className="dispo-label">Answering Machine</div>
            </div>
            <div className="dashboard-dispo-card ni">
              <div className="dispo-icon"><PhoneOff size={18} /></div>
              <div className="dispo-value">{callTotals.ni.toLocaleString()}</div>
              <div className="dispo-label">Not Interested</div>
            </div>
            <div className="dashboard-dispo-card na">
              <div className="dispo-icon"><PhoneMissed size={18} /></div>
              <div className="dispo-value">{callTotals.na.toLocaleString()}</div>
              <div className="dispo-label">No Answer</div>
            </div>
            <div className="dashboard-dispo-card dnc">
              <div className="dispo-icon"><Ban size={18} /></div>
              <div className="dispo-value">{callTotals.dnc.toLocaleString()}</div>
              <div className="dispo-label">DNC</div>
            </div>
            <div className="dashboard-dispo-card dnq">
              <div className="dispo-icon"><UserX size={18} /></div>
              <div className="dispo-value">{callTotals.dnq.toLocaleString()}</div>
              <div className="dispo-label">DNQ</div>
            </div>
            {callTotals.calls > 0 && (
              <div className="dashboard-dispo-card rate">
                <div className="dispo-icon"><TrendingUp size={18} /></div>
                <div className="dispo-value">
                  {((callTotals.xfer / callTotals.calls) * 100).toFixed(1)}%
                </div>
                <div className="dispo-label">Transfer Rate</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Two Column Layout: Billing & Quick Stats */}
      <div className="dashboard-two-col">
        {/* Billing Summary */}
        <div className="client-table-container">
          <div className="client-table-header">
            <h2>Billing Summary</h2>
          </div>
          <div className="billing-summary-content">
            <div className="billing-summary-row">
              <span className="billing-label">Monthly Cost</span>
              <span className="billing-value">{formatCurrency(billing?.monthly_cost)}</span>
            </div>
            <div className="billing-summary-row">
              <span className="billing-label">Daily Cost</span>
              <span className="billing-value">{formatCurrency(billing?.daily_cost)}</span>
            </div>
            <div className="billing-summary-row">
              <span className="billing-label">Days Remaining</span>
              <span className={`billing-value ${billing?.days_remaining <= 7 ? 'warning' : ''}`}>
                {billing?.days_remaining || 0} days
              </span>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="client-table-container">
          <div className="client-table-header">
            <h2>Quick Stats</h2>
          </div>
          <div className="billing-summary-content">
            <div className="billing-summary-row">
              <span className="billing-label">Total Dialers</span>
              <span className="billing-value">{dialers.length}</span>
            </div>
            <div className="billing-summary-row">
              <span className="billing-label">Total Bots</span>
              <span className="billing-value">{totalBots}</span>
            </div>
            <div className="billing-summary-row">
              <span className="billing-label">Pending Requests</span>
              <span className={`billing-value ${requests.length > 0 ? 'warning' : ''}`}>
                {requests.length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Dialers List */}
      <div className="client-section">
        <div className="client-table-container">
          <div className="client-table-header">
            <h2>Your Dialers</h2>
          </div>
          <table className="client-table">
            <thead>
              <tr>
                <th>Dialer</th>
                <th>Campaign</th>
                <th>Bots</th>
                <th>Rate</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {dialers.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                    No dialers assigned to your account
                  </td>
                </tr>
              ) : (
                dialers.map(dialer => (
                  <tr key={dialer.id}>
                    <td>
                      <div className="dialer-name">{dialer.name}</div>
                    </td>
                    <td>{dialer.campaign || '-'}</td>
                    <td>
                      <span className="bot-count">
                        {dialer.active_bots} / {dialer.total_bots}
                      </span>
                    </td>
                    <td>{formatCurrency(dialer.bot_price)}/bot</td>
                    <td>
                      <span className={`client-status ${dialer.active_bots > 0 ? 'online' : 'offline'}`}>
                        <span className="client-status-dot"></span>
                        {dialer.active_bots > 0 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sample Recordings - one pitch audio per campaign so client hears every campaign's pitch */}
      <div className="client-section">
        <div className="client-table-container">
          <div className="client-table-header">
            <h2>Sample Recordings</h2>
            <span className="client-table-subtitle">One pitch sample per campaign</span>
          </div>
          {sampleRecordings.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
              No recordings available yet
            </div>
          ) : (
            <div className="sample-recordings-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '16px',
              padding: '16px'
            }}>
              {sampleRecordings.map((rec, idx) => {
                const audioSrc = rec.audio_url
                  ? `https://dashboard.byredstone.com${rec.audio_url}`
                  : rec.recording_url
                const durationSec = rec.duration_ms
                  ? Math.round(rec.duration_ms / 1000)
                  : rec.recording_duration
                const cardKey = rec.dialer_id ? `d-${rec.dialer_id}` : `v-${rec.voice_set_id}-${idx}`
                return (
                  <div key={cardKey} className="sample-recording-card" style={{
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '14px 16px',
                    background: 'var(--bg-card)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <Music size={18} style={{ color: 'var(--primary)' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {rec.category_name || rec.voice_set_name}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {rec.voice_set_name}
                          {rec.gender ? ` • ${rec.gender}` : ''}
                          {durationSec ? ` • ${durationSec}s` : ''}
                        </div>
                      </div>
                    </div>
                    <audio
                      src={audioSrc}
                      controls
                      preload="none"
                      style={{ width: '100%', height: '36px' }}
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
